import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api, type BriefingView, type CardView, type EntryChange } from '../api';
import { changeBadge, changeOf, changeParts, editionLabel, shortLabel } from '../editions';
import { formatLocalDayShort, formatTime, localToday, plural } from '../format';
import { useAsync } from './ui';

/**
 * The day's editions as a strip: each slot with its time, linked when published,
 * named as upcoming or missed otherwise. The edition on screen is marked. Older
 * backends without the editions endpoint simply get no strip.
 */
export function EditionStrip({ view }: { view: BriefingView }) {
  const b = view.briefing!;
  const day = useAsync(() => api.editionDay(b.edition_date), [b.edition_date]);
  const nav = view.navigation;
  const slots = day.data?.slots ?? [];
  const list = useRef<HTMLOListElement>(null);
  // On a phone the strip is one scrollable row; keep the edition shown in view.
  useEffect(() => {
    const cur = list.current?.querySelector<HTMLElement>('.is-current');
    if (cur && typeof cur.scrollIntoView === 'function') cur.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [slots.length, b.id]);
  if (day.error && !nav) return null;
  return (
    <nav className="edition-strip" aria-label={`Ausgaben vom ${formatLocalDayShort(b.edition_date)}`}>
      {nav?.previous ? (
        <Link className="edition-step" to={`/ausgaben/${nav.previous.id}`} title={`${editionLabel(nav.previous.edition_type, nav.previous.label)}, ${formatLocalDayShort(nav.previous.edition_date)}`}>
          <span aria-hidden="true">‹</span>
          <span className="visually-hidden">Vorherige Ausgabe: {editionLabel(nav.previous.edition_type, nav.previous.label)}</span>
        </Link>
      ) : (
        <span className="edition-step is-disabled" aria-hidden="true">
          ‹
        </span>
      )}
      <ol className="edition-slots" ref={list}>
        {slots.map((s) => {
          const current = s.briefing_id === b.id;
          const body = (
            <>
              <span className="slot-time">{s.at || (s.published_at ? formatTime(s.published_at) : '')}</span>
              <span className="slot-name">{shortLabel(s.edition_type || s.slot)}</span>
              {s.state !== 'published' && <span className="slot-state">{stateText(s.state)}</span>}
              {s.state === 'published' && s.quiet && <span className="slot-state">ruhig</span>}
            </>
          );
          return (
            <li key={s.slot} className={`slot slot-${s.state}${current ? ' is-current' : ''}`}>
              {s.state === 'published' && s.briefing_id ? (
                <Link to={`/ausgaben/${s.briefing_id}`} aria-current={current ? 'page' : undefined} aria-label={`${s.label} ${s.at}${current ? ', angezeigt' : ''}${s.quiet ? ', ruhig' : ''}`}>
                  {body}
                </Link>
              ) : (
                <span aria-label={`${s.label} ${s.at}: ${stateText(s.state)}`}>{body}</span>
              )}
            </li>
          );
        })}
      </ol>
      {nav?.next ? (
        <Link className="edition-step" to={`/ausgaben/${nav.next.id}`} title={`${editionLabel(nav.next.edition_type, nav.next.label)}, ${formatLocalDayShort(nav.next.edition_date)}`}>
          <span aria-hidden="true">›</span>
          <span className="visually-hidden">Nächste Ausgabe: {editionLabel(nav.next.edition_type, nav.next.label)}</span>
        </Link>
      ) : (
        <span className="edition-step is-disabled" aria-hidden="true">
          ›
        </span>
      )}
    </nav>
  );
}

function stateText(state: string): string {
  switch (state) {
    case 'due':
      return 'in Vorbereitung';
    case 'missed':
      return 'ausgefallen';
    case 'upcoming':
      return 'folgt';
    default:
      return '';
  }
}

/**
 * What the edition covers, stated plainly: since when it looks for news, the
 * newest content it could use, and what it found.
 */
export function CoverageLine({ view }: { view: BriefingView }) {
  const c = view.briefing?.completeness;
  if (!c || !c.coverage_from) return null;
  const d = c.delta;
  const prev = c.previous;
  const sameDay = localToday(new Date(c.coverage_from)) === view.briefing!.edition_date;
  const at = sameDay ? formatTime(c.coverage_from) : `${formatLocalDayShort(localToday(new Date(c.coverage_from)))} ${formatTime(c.coverage_from)}`;
  const since = prev ? `${editionLabel(prev.edition_type)} (${at})` : null;
  const found = d
    ? [
        d.new > 0 ? plural(d.new, 'neue Meldung', 'neue Meldungen') : null,
        d.updates > 0 ? plural(d.updates, 'Aktualisierung', 'Aktualisierungen') : null,
      ].filter(Boolean)
    : [];
  return (
    <p className="coverage-line">
      {since ? `Neu seit ${since}` : `Erste Ausgabe · Meldungen seit ${at}`} · Stand {formatTime(c.cutoff_at)}
      {c.newest_content_at ? ` · neueste Meldung erfasst ${formatTime(c.newest_content_at)}` : ''}
      {found.length > 0 ? ` · ${found.join(', ')}` : ''}
    </p>
  );
}

/**
 * Content that reached Hugarheim after this edition was prepared. It is not in
 * the edition, and the edition does not pretend it is: the next slot judges it.
 */
export function SinceCutoffNotice({ view }: { view: BriefingView }) {
  const s = view.since_cutoff;
  if (!s || (s.news_versions === 0 && !s.crossfit_version_id)) return null;
  const next = s.next_label ? ` (${s.next_label}${s.next_at ? `, ${formatTime(s.next_at)}` : ''})` : '';
  return (
    <div className="notice notice-info since-cutoff" role="note">
      <strong>Seit Stand {formatTime(s.cutoff_at)} eingegangen – nicht in dieser Ausgabe</strong>
      <ul>
        {s.crossfit_version_id && (
          <li>
            Neue Trainingsdaten für heute{s.crossfit_at ? ` (erfasst ${formatTime(s.crossfit_at)})` : ''}.{' '}
            <Link to="/crossfit">CrossFit öffnen</Link>
          </li>
        )}
        {s.news_versions > 0 && (
          <li>
            {plural(s.news_versions, 'neue oder geänderte Meldung', 'neue oder geänderte Meldungen')}; die nächste Ausgabe{next} prüft, was davon neu ist.
          </li>
        )}
      </ul>
    </div>
  );
}

/** The honest short edition: nothing new, said plainly, with what is still there. */
export function QuietNotice({ view }: { view: BriefingView }) {
  const c = view.briefing?.completeness;
  if (!c?.quiet) return null;
  const d = c.delta;
  const known = d ? d.repeats + d.immaterial : 0;
  return (
    <div className="quiet-edition" role="status">
      <strong>Seit der letzten Ausgabe nichts wesentlich Neues.</strong>
      <p>
        {known > 0
          ? `${plural(known, 'bekannte Meldung', 'bekannte Meldungen')} ${known === 1 ? 'wurde' : 'wurden'} geprüft; ${
              d && d.immaterial > 0 ? `${plural(d.immaterial, 'erhielt', 'erhielten')} nur weitere Berichte ohne neue Fakten oder Umformulierungen, ` : ''
            }nichts davon ist eine neue Entwicklung.`
          : 'In diesem Zeitraum kamen keine Meldungen hinzu.'}{' '}
        Frühere Meldungen findest du in der vorherigen Ausgabe und in der <Link to="/suche">Suche</Link>.
      </p>
    </div>
  );
}

/**
 * Why a returning story is in this edition, where the reader meets it: on the
 * front-page card and on the tail row alike, with the complete "Was ist neu"
 * list. Developments and added reports are separate lists: another source
 * alone does not show that anything happened. The baseline is named for what
 * it is, the version an earlier edition carried; it is not claimed that the
 * reader read it. A new story carries no badge: the edition header already
 * says everything is new since the previous edition.
 */
export function ChangeLine({ view, compact = false }: { view: CardView; compact?: boolean }) {
  const change = changeOf(view);
  const badge = changeBadge(change);
  // Compact rows of the context block sit under their own heading already.
  if (!badge || badge.kind === 'new' || (compact && badge.kind === 'context' && change?.basis === 'background')) return null;
  const { developments, reports } = changeParts(change);
  const showsDelta = badge.kind === 'update' || badge.kind === 'report';
  return (
    <div className={`change-line change-${badge.kind}${compact ? ' is-compact' : ''}`}>
      <span className="change-badge">{badge.label}</span>
      {showsDelta && developments.length > 0 && (
        <div className="change-block">
          <span className="mini-h">Was ist neu</span>
          <ul className="change-what" aria-label="Was ist neu">
            {developments.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
      {showsDelta && reports.length > 0 && (
        <div className="change-block">
          <span className="mini-h">{reports.length === 1 ? 'Neue Quelle' : 'Neue Quellen'}</span>
          <ul className="change-sources" aria-label="Neue Quellen">
            {reports.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
          {badge.kind === 'report' && (
            <span className="change-note">Ein weiterer Bericht; ohne Zusammenfassung ist nicht geprüft, ob er neue Fakten enthält.</span>
          )}
        </div>
      )}
      {change?.basis === 'recapture_unchanged' && (
        <span className="change-note">
          Neu gelesen{change.captured_at ? ` ${formatTime(change.captured_at)}` : ''}: Workout und Buchungen unverändert.
        </span>
      )}
      {change?.basis === 'new_capture' && badge.kind === 'context' && (
        <span className="change-note">Neuere Erfassung; diese Ausgabe hat Workout und Buchungen nicht verglichen.</span>
      )}
      {showsDelta && change?.previous_shown_at && <span className="change-since">Vergleich mit der Version aus {previousWhen(change)}</span>}
    </div>
  );
}

function previousWhen(change: EntryChange): string {
  const label = change.previous_edition ? editionLabel(change.previous_edition) : 'einer früheren Ausgabe';
  const at = change.previous_shown_at ? new Date(change.previous_shown_at) : null;
  const day = at && localToday(at) !== localToday() ? `${formatLocalDayShort(localToday(at))} ` : '';
  return `der ${label}${at ? ` (${day}${formatTime(change.previous_shown_at)})` : ''}`;
}
